import { HttpClient, Logger, LogLevel } from '@openticket/lib-shop';

const EXPECTED_QUEUE_IT_JS_VERSION = '2.0.52';

interface QueueItWaitingRoomConfig {
    queueItClientLoaded: boolean;
    queueItCustomerId: string;
    queueItEventId: string;
    queueItHost: string;
}

export function registerQueueIt(
    log: Logger,
    shopApiUrl: string,
    http?: HttpClient | undefined
): boolean {
    if (!http) {
        return false;
    }

    http.interceptors.response.use(response => {
        if (
            response.status === 403 &&
            new URL(response.config.url || '').hostname ===
                new URL(shopApiUrl).hostname
        ) {
            const queueItConfig = getQueueItConfigValues();
            // Log about the block
            log.create(
                LogLevel.Error,
                ['queueit', 'request', 'blocked'],
                {
                    queueit_client_loaded: queueItConfig.queueItClientLoaded,
                    queueit_customer_id: queueItConfig.queueItCustomerId,
                    queueit_event_id: queueItConfig.queueItEventId,
                    queueit_host: queueItConfig.queueItHost,
                    request_url: response.config.url || '',
                },
                'Gatekeeper blocked request.'
            );
            throw Error('Gatekeeper blocked request.');
        }
        return response;
    });

    http.interceptors.request.use(request => {
        const apiHost = new URL(shopApiUrl).hostname;
        const interceptedHost = new URL(request.url || '').hostname;
        // Only inject for known host
        if (apiHost && interceptedHost === apiHost) {
            const queueItHeader = getQueueItHeader(
                window.initialLocationHref,
                log,
                request.url
            );
            if (queueItHeader) {
                request.headers.set(queueItHeader[0], queueItHeader[1]);
            }
        }
        return request;
    });

    return true;
}

function queueItConfigReady(log?: Logger): boolean {
    if (
        !window.QueueIt ||
        typeof window.QueueIt !== 'object' ||
        !('isIntegrationConfigReady' in window.QueueIt) ||
        typeof window.QueueIt.isIntegrationConfigReady !== 'function' ||
        !window.QueueIt.isIntegrationConfigReady()
    ) {
        return false;
    }

    // DD-4E5D: Self hosting QueueItClient js & guarding for expected version
    // Please note: the Javascript version could also not YET be set here; not logging if no value
    if (
        'Javascript' in window.QueueIt &&
        typeof window.QueueIt.Javascript === 'object' &&
        window.QueueIt.Javascript &&
        'Version' in window.QueueIt.Javascript &&
        typeof window.QueueIt.Javascript.Version === 'string' &&
        window.QueueIt.Javascript.Version !== EXPECTED_QUEUE_IT_JS_VERSION &&
        log
    ) {
        log.create(
            LogLevel.Error,
            ['queueit', 'version', 'unexpected'],
            {
                queueit_reported_version: window.QueueIt.Javascript.Version,
                queueit_expected_version: EXPECTED_QUEUE_IT_JS_VERSION,
            },
            'Unexpected QueueIt version.'
        );
    }

    return !!(
        window.myQueueClient &&
        typeof window.myQueueClient === 'object' &&
        window.myQueueClient.customerId &&
        window.myQueueClient.eventId &&
        window.myQueueClient.options &&
        typeof window.myQueueClient.options === 'object' &&
        window.myQueueClient.options.host
    );
}

function getQueueItHeader(
    initialLocation: string,
    log: Logger,
    requestUrl = ''
): [string, string] | null {
    const initialLocationUrl = new URL(initialLocation);
    initialLocationUrl.searchParams.set('sessionId', log.id);

    const queryParamQueueItToken = (
        initialLocationUrl.searchParams.get('queueittoken') || ''
    ).trim();

    if (!queueItConfigReady(log)) {
        // DD-4E58 Intentionally not logging here since the shopdata call is
        // now in race condition state with QueueIt init and this will happen
        return null;
    }

    const queueItConfig = getQueueItConfigValues();
    if (queryParamQueueItToken) {
        // DD-4E51 This is the wanted flow.
        // This token is only legitimately present when the visitor arrived here from a QueueIt waitingroom.
        // (This waiting room need not take up any time)
        logValidQueueItTokenFound(
            log,
            queryParamQueueItToken,
            queueItConfig,
            requestUrl
        );

        return ['x-queueit-ajaxpageurl', queryParamQueueItToken];
    }

    // No token found in url, try to get from cookie
    const queueItCookieToken = getQueueItCookie();
    if (queueItCookieToken) {
        logValidQueueItCookieFound(
            log,
            queueItCookieToken,
            queueItConfig,
            requestUrl
        );

        return ['x-queueit-cookies', queueItCookieToken];
    }

    // DD-4E57 QueueIt header is missing, this is in some cases a recoverable error (when the shop isn't blacklisted)
    // The user should be able to recover
    logMissingQueueItHeader(log, queueItConfig, requestUrl);

    return null;
}

function logValidQueueItTokenFound(
    log: Logger,
    queueItToken: string,
    queueItConfig: QueueItWaitingRoomConfig,
    requestUrl = ''
): void {
    const queueItTokenValues = Object.fromEntries(
        // e_{waiting room id}
        // q_{uuid...}
        // ts_{seconds since 1970-01-01 00:00:00 UTC...}
        // ce_true
        // rt_queue
        // h_{09af...}
        queueItToken.split('~').map((kv: string) => {
            const [k, ...v] = kv.split('_');
            return [k, v.join('_')];
        })
    );

    log.create(
        LogLevel.Info,
        ['queueit', 'token', 'found'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,

            queueit_token_event_id: queueItTokenValues.e || '',
            queueit_token_ts: queueItTokenValues.ts || '',
            queueit_token_redirect_type: queueItTokenValues.rt || '',

            request_url: requestUrl,
        },
        'QueueIt token found'
    );
}

function logValidQueueItCookieFound(
    log: Logger,
    queueItCookie: string,
    queueItConfig: QueueItWaitingRoomConfig,
    requestUrl = ''
): void {
    log.create(
        LogLevel.Info,
        ['queueit', 'cookie', 'found'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,
            queueit_cookie: queueItCookie || '',
            request_url: requestUrl,
        },
        'QueueIt cookie found'
    );
}

function logMissingQueueItHeader(
    log: Logger,
    queueItConfig: QueueItWaitingRoomConfig,
    requestUrl = ''
): void {
    log.create(
        LogLevel.Warning,
        ['queueit', 'token', 'missing', 'error'],
        {
            queueit_client_loaded: queueItConfig.queueItClientLoaded,
            queueit_customer_id: queueItConfig.queueItCustomerId,
            queueit_event_id: queueItConfig.queueItEventId,
            queueit_host: queueItConfig.queueItHost,
            request_url: requestUrl,
        },
        'QueueIt token missing for waiting room.'
    );
}

function getQueueItConfigValues(): QueueItWaitingRoomConfig {
    return {
        queueItClientLoaded: !!window.myQueueClient,
        queueItCustomerId:
            (window.myQueueClient &&
                typeof window.myQueueClient === 'object' &&
                window.myQueueClient.customerId) ||
            '',
        queueItEventId:
            (window.myQueueClient &&
                typeof window.myQueueClient === 'object' &&
                window.myQueueClient.eventId) ||
            '',
        queueItHost:
            (window.myQueueClient &&
                typeof window.myQueueClient === 'object' &&
                window.myQueueClient.options &&
                typeof window.myQueueClient.options === 'object' &&
                window.myQueueClient.options.host) ||
            '',
    };
}

export function getQueueItCookie(): string | null {
    try {
        // Use QueueItClient to get the most appropriate cookie
        if (
            'myQueueClient' in window &&
            window.myQueueClient &&
            typeof window.myQueueClient === 'object' &&
            'cookieManager' in window.myQueueClient &&
            window.myQueueClient.cookieManager &&
            typeof window.myQueueClient.cookieManager === 'object' &&
            'getQueueStateCookie' in window.myQueueClient.cookieManager &&
            typeof window.myQueueClient.cookieManager.getQueueStateCookie ===
                'function'
        ) {
            const cookie = window.myQueueClient.cookieManager.getQueueStateCookie();
            if (cookie !== undefined && cookie !== null && cookie !== '') {
                return cookie;
            }
        }
    } catch {
        /* No-op */
    }
    return null;
}
